import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewContainerRef,
} from '@angular/core';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { TranslatePipe } from '@recruitee/i18n';
import { DialogService, DialogWidth, DropdownItemComponent } from '@recruitee/user-interface';

import { IntegrationsMailboxLinkInsertedActionToken } from '../../../integrations-mailbox-link-inserted-action-token';
import { RedactorInsertsDialogComponent } from '../../../wysiwyg-extensions/link/redactor-inserts-dialog/redactor-inserts-dialog.component';
import { WysiwygLink, WysiwygLinkType } from '../../../wysiwyg-extensions/link/wysiwyg-link.types';
import { RedactorInsertsDropdownHeaderComponent } from '../redactor-inserts-dropdown-header/redactor-inserts-dropdown-header.component';

@Component({
  selector: 'rt-redactor-inserts-integrations-kununu',
  templateUrl: 'redactor-inserts-integrations-kununu.component.html',
  styleUrls: ['./redactor-inserts-integrations-kununu.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RedactorInsertsDropdownHeaderComponent, DropdownItemComponent, TranslatePipe],
})
export class RedactorInsertsIntegrationsKununuComponent {
  @Input() public hasBackAction: boolean = true;

  @Output() public back: EventEmitter<void> = new EventEmitter();
  @Output() public closeDropdown: EventEmitter<void> = new EventEmitter();
  @Output() public insertLink: EventEmitter<WysiwygLink> = new EventEmitter();

  public canManageIntegrations = false;

  public mailboxLinkInsertedEventAction = inject(IntegrationsMailboxLinkInsertedActionToken);

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private store: Store,
    private vcRef: ViewContainerRef,
    private dialogService: DialogService,
  ) {}

  public ngOnInit(): void {
    this.store
      .pipe(
        select(AbilitiesSelectors.hasOne(Ability.ManageIntegrations)),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(enabled => {
        this.canManageIntegrations = enabled;
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public insertApplicantFeedback(): void {
    this.insertFeedback(WysiwygLinkType.IntegrationsKununuApplicantFeedback);
  }

  public insertEmployeeFeedback(): void {
    this.insertFeedback(WysiwygLinkType.IntegrationsKununuEmployeeFeedback);
  }

  private insertFeedback(
    linkType:
      | WysiwygLinkType.IntegrationsKununuApplicantFeedback
      | WysiwygLinkType.IntegrationsKununuEmployeeFeedback,
  ) {
    this.dialogService
      .openCustom(
        RedactorInsertsDialogComponent,
        { width: DialogWidth.Small },
        {
          type: linkType,
          link: `[${linkType}_url]`,
          label: '',
          isEdit: false,
          expirationDays: 0,
        },
        this.vcRef,
      )
      .afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(linkText => {
        if (linkText) {
          this.insertLink.emit({
            type: linkType,
            url: `[${linkType}_url]`,
            label: linkText.label,
            name: linkText.label,
            target: 'blank',
          });

          this.store.dispatch(this.mailboxLinkInsertedEventAction);
        }
        this.closeDropdown.emit();
      });
  }
}
